import { useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { AlertTriangle, FileText, Loader2 } from "lucide-react";

const label = "text-[11px] font-semibold uppercase tracking-wide text-slate-500";
const money = (v, d = 2) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d })}`;

export default function InvoiceReviewDialog({ parsed, pos, suppliers, components, onClose, onSaved }) {
  const [f, setF] = useState(() => ({
    po_id: parsed.matched_po_id || "",
    supplier_id: parsed.supplier_id || "",
    invoice_number: parsed.invoice_number || "",
    invoice_date: parsed.invoice_date || "",
    due_date: parsed.due_date || "",
    payment_terms: parsed.payment_terms || "",
    amount: parsed.total_amount ? String(parsed.total_amount) : "",
    notes: "",
  }));
  const [lines, setLines] = useState(() => parsed.lines.map((l, i) => ({ ...l, _i: i, skip: false })));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setLine = (i, patch) => setLines((prev) => prev.map((l) => {
    if (l._i !== i) return l;
    const next = { ...l, ...patch };
    const units = Number(next.total_units) || 0;
    next.unit_cost = units > 0 && next.line_total > 0 ? next.line_total / units : 0;
    return next;
  }));

  const po = pos.find((p) => p.id === f.po_id) || null;
  const poOptions = useMemo(() => {
    const cand = parsed.candidates || [];
    const candIds = new Set(cand.map((c) => c.id));
    const rest = pos.filter((p) => !candIds.has(p.id) && p.status !== "cancelled");
    return [...cand.map((c) => ({ ...c, cand: true })), ...rest.map((p) => ({ id: p.id, po_number: p.po_number, supplier_name: p.supplier_name, total: p.total }))];
  }, [parsed.candidates, pos]);

  const amountNum = Number(f.amount) || 0;
  const mismatch = po && amountNum > 0 && po.total > 0 && Math.abs(po.total - amountNum) > Math.max(1, po.total * 0.02);

  const compValue = (l) => (l.component_id ? `${l.component_id}|${l.type_id || ""}` : "none");
  const onCompPick = (i, v) => {
    if (v === "none") { setLine(i, { component_id: "", component_name: "", type_id: "", type_name: "" }); return; }
    const [cid, tid] = v.split("|");
    const c = components.find((x) => x.id === cid);
    const t = (c?.types || []).find((x) => x.id === tid);
    setLine(i, { component_id: cid, component_name: c?.name || "", type_id: tid || "", type_name: t?.name || "" });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/purchase-orders/save-parsed-invoice", {
        po_id: f.po_id, supplier_id: f.supplier_id,
        invoice_number: f.invoice_number, invoice_date: f.invoice_date, due_date: f.due_date,
        payment_terms: f.payment_terms, amount: amountNum, notes: f.notes,
        docs: parsed.doc?.path ? [parsed.doc] : [],
        cost_lines: lines.map(({ _i, ...l }) => ({ ...l, total_units: Number(l.total_units) || 0 })),
      });
      toast.success(`Invoice ${data.invoice_number} filed${data.po_number ? ` under ${data.po_number}` : " (no PO)"} — ${data.cost_lines} line${data.cost_lines !== 1 ? "s" : ""} counted toward avg cost`, { duration: 9000 });
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't save the invoice");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto rounded-sm" data-testid="invoice-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl font-bold">
            <FileText className="h-5 w-5 text-[#002FA7]" /> Review Invoice — {parsed.doc?.name}
          </DialogTitle>
        </DialogHeader>

        {parsed.duplicate && (
          <p className="flex items-center gap-1.5 rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700" data-testid="invoice-duplicate-warning">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> An invoice with this number already exists for this supplier — saving will be blocked.
          </p>
        )}
        {mismatch && (
          <p className="flex items-center gap-1.5 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700" data-testid="invoice-mismatch-warning">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Invoice total {money(amountNum)} differs from PO total {money(po.total)} — double-check before saving.
          </p>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#002FA7]">What we read</p>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1 col-span-2">
                <Label className={label}>Supplier</Label>
                <Select value={f.supplier_id || "none"} onValueChange={(v) => set("supplier_id", v === "none" ? "" : v)}>
                  <SelectTrigger className="h-9 rounded-sm" data-testid="invoice-review-supplier"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Pick supplier —</SelectItem>
                    {suppliers.map((s) => (<SelectItem key={s.id} value={s.id}>{s.company_name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className={label}>Invoice #</Label>
                <Input value={f.invoice_number} onChange={(e) => set("invoice_number", e.target.value)} className="h-9 rounded-sm" data-testid="invoice-review-number" />
              </div>
              <div className="space-y-1">
                <Label className={label}>Amount</Label>
                <Input type="text" inputMode="decimal" value={f.amount} onChange={(e) => set("amount", e.target.value.replace(/[^0-9.]/g, ""))} className="h-9 rounded-sm" data-testid="invoice-review-amount" />
              </div>
              <div className="space-y-1">
                <Label className={label}>Invoice Date</Label>
                <Input type="date" value={f.invoice_date} onChange={(e) => set("invoice_date", e.target.value)} className="h-9 rounded-sm" data-testid="invoice-review-date" />
              </div>
              <div className="space-y-1">
                <Label className={label}>Due Date</Label>
                <Input type="date" value={f.due_date} onChange={(e) => set("due_date", e.target.value)} className="h-9 rounded-sm" data-testid="invoice-review-due" />
              </div>
              <div className="space-y-1 col-span-2">
                <Label className={label}>Payment Terms</Label>
                <Input value={f.payment_terms} onChange={(e) => set("payment_terms", e.target.value)} placeholder="NET 30" className="h-9 rounded-sm" data-testid="invoice-review-terms" />
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#002FA7]">Matched purchase order</p>
            <Select value={f.po_id || "none"} onValueChange={(v) => set("po_id", v === "none" ? "" : v)}>
              <SelectTrigger className="h-9 rounded-sm" data-testid="invoice-review-po"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— No PO (standalone invoice) —</SelectItem>
                {poOptions.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.po_number} · {p.supplier_name} · {money(p.total)}{p.cand ? " ★" : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {po ? (
              <div className="space-y-1 rounded-sm border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="invoice-review-po-summary">
                <p className="font-heading text-sm font-bold text-slate-900">{po.po_number} — {po.supplier_name}</p>
                <p>PO total <span className="font-semibold text-slate-800">{money(po.total)}</span> · Status <span className="font-semibold uppercase">{po.status}</span>{po.order_date ? ` · Ordered ${po.order_date}` : ""}</p>
                {parsed.po_reference && <p>Invoice references PO <span className="font-semibold">{parsed.po_reference}</span></p>}
              </div>
            ) : (
              <p className="rounded-sm border border-dashed border-slate-300 p-3 text-xs text-slate-400">
                {parsed.po_reference ? `Invoice references "${parsed.po_reference}" but no PO matched — pick one above or save without.` : "No PO reference found — pick one above or save as standalone."}
              </p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#002FA7]">Line items & per-unit cost</p>
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-slate-500">
                <th className="border border-slate-200 bg-slate-50 px-2 py-1.5 text-left">Invoice line</th>
                <th className="border border-slate-200 bg-slate-50 px-2 py-1.5 text-left">Component</th>
                <th className="border border-slate-200 bg-slate-50 px-2 py-1.5 text-right">Qty</th>
                <th className="border border-slate-200 bg-slate-50 px-2 py-1.5 text-right">Line $</th>
                <th className="w-28 border border-slate-200 bg-slate-50 px-2 py-1.5 text-right">Total units</th>
                <th className="border border-slate-200 bg-slate-50 px-2 py-1.5 text-right">Per-unit</th>
                <th className="w-12 border border-slate-200 bg-slate-50 px-2 py-1.5 text-center">Skip</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l._i} className={`align-top ${l.skip || l.is_freight ? "opacity-50" : ""}`} data-testid={`invoice-line-${l._i}`}>
                  <td className="max-w-[220px] border border-slate-200 px-2 py-1.5 text-slate-600">
                    <span className="line-clamp-2">{l.description || "—"}</span>
                    {l.is_freight && <span className="mt-0.5 inline-block rounded-sm bg-slate-100 px-1 text-[9px] font-bold uppercase text-slate-500">Freight — excluded</span>}
                  </td>
                  <td className="border border-slate-200 px-2 py-1.5">
                    {l.is_freight ? <span className="text-slate-400">—</span> : (
                      <Select value={compValue(l)} onValueChange={(v) => onCompPick(l._i, v)}>
                        <SelectTrigger className="h-7 rounded-sm text-xs" data-testid={`invoice-line-comp-${l._i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— Not a component —</SelectItem>
                          {components.map((c) => (c.types || []).map((t) => (
                            <SelectItem key={`${c.id}|${t.id}`} value={`${c.id}|${t.id}`}>{c.name} — {t.name}</SelectItem>
                          )))}
                        </SelectContent>
                      </Select>
                    )}
                  </td>
                  <td className="whitespace-nowrap border border-slate-200 px-2 py-1.5 text-right text-slate-600">{Number(l.quantity || 0).toLocaleString()} {l.uom}</td>
                  <td className="whitespace-nowrap border border-slate-200 px-2 py-1.5 text-right font-medium text-slate-700">{money(l.line_total)}</td>
                  <td className="border border-slate-200 px-2 py-1.5">
                    {l.is_freight ? <span className="block text-right text-slate-400">—</span> : (
                      <Input type="text" inputMode="numeric" value={l.total_units || ""} placeholder="units"
                        onChange={(e) => setLine(l._i, { total_units: e.target.value.replace(/[^0-9.]/g, "") })}
                        className="h-7 rounded-sm text-right text-xs" data-testid={`invoice-line-units-${l._i}`} />
                    )}
                  </td>
                  <td className="whitespace-nowrap border border-slate-200 px-2 py-1.5 text-right font-semibold tabular-nums text-[#002FA7]" data-testid={`invoice-line-unitcost-${l._i}`}>
                    {!l.is_freight && l.unit_cost > 0 ? `$${Number(l.unit_cost).toFixed(4)}` : "—"}
                  </td>
                  <td className="border border-slate-200 px-2 py-1.5 text-center">
                    {!l.is_freight && (
                      <Checkbox checked={l.skip} onCheckedChange={(v) => setLine(l._i, { skip: !!v })} data-testid={`invoice-line-skip-${l._i}`} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[10px] text-slate-400">Per-unit = line $ ÷ total units. Skipped and freight lines stay on the invoice total but don't count toward component average cost.</p>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm" data-testid="invoice-review-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || parsed.duplicate} className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="invoice-review-save">
            {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Save Invoice
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
